import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useOperatorContext } from "@src/context/operatorContext";
import { AppUtils } from "@src/utils";
import Icon from "./Icon";
import useCarouselDrag from "@src/components/utility/CarouselDragHook";

const InfiniteCarousel = ({ children, initialIndex = 0 }) => {
  const childrenArray = React.Children.toArray(children);
  const totalCards = childrenArray.length;
  // Create cloned elements for seamless transition
  const extendedChildren = [
    ...childrenArray,
    ...childrenArray,
    ...childrenArray,
  ];

  const [activeIndex, setActiveIndex] = useState(
    childrenArray.length + initialIndex,
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  const [itemWidth, setItemWidth] = useState(null);

  useEffect(() => {
    if (itemRefs.current.length > 0) {
      setItemWidth(itemRefs.current[0]?.offsetWidth || 250);
    }
  }, [children]);

  const goToClosestCard = (index) => {
    setIsTransitioning(true);

    const positions = [];
    for (let i = 0; i < extendedChildren.length; i++) {
      if (i % totalCards === index) positions.push(i);
    }

    const closestIndex = positions.reduce((prev, curr) =>
      Math.abs(curr - activeIndex) < Math.abs(prev - activeIndex) ? curr : prev,
    );

    setActiveIndex(closestIndex);
  };

  const nextCard = () => {
    setIsTransitioning(true);

    //For the infinite effect, we "shift" the index back to the middle if they're near the start
    const lastMiddleIndex = totalCards * 3 - 2;
    if (activeIndex >= lastMiddleIndex) {
      setActiveIndex((prev) => prev + 1);
      setTimeout(() => {
        const track = containerRef.current.querySelector(".carousel-track");
        track.style.transition = "none";
        setActiveIndex((prev) => totalCards + (prev % totalCards));
        setTimeout(() => {
          track.style.transition = "";
          setIsTransitioning(false);
        }, 0);
      }, 500);
    } else {
      setActiveIndex((prev) => prev + 1);
    }
  };

  const prevCard = () => {
    setIsTransitioning(true);

    //For the infinite effect, we "shift" the index back to the middle if they're near the start
    const firstMiddleIndex = totalCards;
    if (activeIndex <= firstMiddleIndex) {
      setActiveIndex((prev) => prev - 1);
      setTimeout(() => {
        const track = containerRef.current.querySelector(".carousel-track");
        track.style.transition = "none";
        setActiveIndex((prev) => totalCards + (prev % totalCards));
        setTimeout(() => {
          track.style.transition = "";
          setIsTransitioning(false);
        }, 0);
      }, 500);
    } else {
      setActiveIndex((prev) => prev - 1);
    }
  };

  const {
    dragOffset,
    isDragging,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    getSafeClickHandler,
  } = useCarouselDrag({
    itemWidth,
    onSwipeLeft: nextCard,
    onSwipeRight: prevCard,
  });

  return (
    <div className="relative w-full flex flex-col items-center carousel-container">
      {/* Navigation arrows */}
      <div className="absolute top-1/2 transform -translate-y-1/2 w-full flex justify-between px-4">
        <button
          onClick={prevCard}
          className="bg-primary-300 p-2 text-white rounded-full"
        >
          <Icon
            name={"chevronLeft"}
            backgroundColor="transparent"
            size="lg"
            textColor={"white"}
            inline
            centered={true}
            padding={true}
          />
        </button>
        <button
          onClick={nextCard}
          className="bg-primary-300 p-2 text-white rounded-full"
        >
          <Icon
            name={"chevronRight"}
            backgroundColor="transparent"
            size="lg"
            textColor={"white"}
            inline
            centered={true}
            padding={true}
          />
        </button>
      </div>
      {/* Cards wrapper */}
      <div
        className="relative w-full overflow-hidden flex justify-center"
        ref={containerRef}
      >
        <div
          className={`flex items-center carousel-track ${
            isTransitioning ? "carousel-transition" : "carousel-no-transition"
          }`}
          style={{
            transform: `translateX(calc(50% - ${
              activeIndex * (itemWidth + 40) + itemWidth / 2
            }px + ${dragOffset}px))`,
            gap: "40px",
          }}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={() => {
            if (isDragging) handleDragEnd();
          }}
        >
          {extendedChildren.map((child, index) => {
            const childWithHandler =
              React.isValidElement(child) &&
              React.cloneElement(child, {
                onClick: getSafeClickHandler(child.props.onClick),
              });
            return (
              <div
                key={`infinite-carousel-item-${index}`}
                ref={(el) => (itemRefs.current[index] = el)}
                className={`carousel-item transition-opacity ${
                  index === activeIndex ? "active" : ""
                }`}
              >
                {childWithHandler || child}
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation dots */}
      <div className="flex gap-8 mt-4">
        {childrenArray.map((_, index) => (
          <button
            key={index}
            onClick={() => goToClosestCard(index)}
            className={`w-2 h-2 rounded-full carousel-dot ${
              activeIndex % totalCards === index ? "bg-gray-600" : "bg-gray-300"
            }`}
          ></button>
        ))}
      </div>
    </div>
  );
};

const SimpleCarousel = ({ showDots, showArrows, children }) => {
  const childrenArray = React.Children.toArray(children);
  const totalCards = childrenArray.length;
  const shouldShowArrows = !!showArrows || (!showArrows && !showDots);

  const [activeIndex, setActiveIndex] = useState(
    Math.floor(childrenArray.length / 2) % 2 === 0
      ? Math.floor(childrenArray.length / 2) - 1
      : Math.floor(childrenArray.length / 2),
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);

  const [itemWidth, setItemWidth] = useState(null);

  useEffect(() => {
    if (itemRefs.current.length > 0) {
      setItemWidth(itemRefs.current[0]?.offsetWidth || 250);
    }
  }, [children]);

  const goToCard = (index) => {
    setIsTransitioning(true);
    setActiveIndex(index);
  };

  const nextCard = () => {
    setIsTransitioning(true);
    if (activeIndex < totalCards - 1) {
      setActiveIndex((prev) => prev + 1);
    }
  };

  const prevCard = () => {
    setIsTransitioning(true);
    if (activeIndex > 0) {
      setActiveIndex((prev) => prev - 1);
    }
  };

  const {
    dragOffset,
    isDragging,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    getSafeClickHandler,
  } = useCarouselDrag({
    itemWidth,
    onSwipeLeft: nextCard,
    onSwipeRight: prevCard,
    onSnapBack: () => goToCard(activeIndex % totalCards),
  });

  return (
    <div className="relative w-full flex flex-col items-center carousel-container">
      {/* Cards wrapper */}
      <div
        className="relative w-full overflow-hidden flex justify-center"
        ref={containerRef}
      >
        <div
          className={`flex items-center carousel-track ${
            isTransitioning ? "carousel-transition" : "carousel-no-transition"
          }`}
          style={{
            transform: `translateX(calc(50% - ${
              activeIndex * (itemWidth + 40) + itemWidth / 2
            }px + ${dragOffset}px))`,
            gap: "40px",
            cursor: isDragging.current ? "grabbing" : "grab",
          }}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={() => {
            if (isDragging.current) handleDragEnd();
          }}
        >
          {childrenArray.map((child, index) => {
            const childWithHandler =
              React.isValidElement(child) &&
              React.cloneElement(child, {
                onClick: getSafeClickHandler(child.props.onClick),
              });

            return (
              <div
                key={`simple-carousel-item-${index}`}
                ref={(el) => (itemRefs.current[index] = el)}
                className={`carousel-item transition-opacity ${
                  index === activeIndex ? "active" : ""
                }`}
              >
                {childWithHandler || child}
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation dots */}
      {!!showDots && (
        <div className="flex gap-8 mt-4">
          {childrenArray.map((_, index) => (
            <button
              key={index}
              onClick={() => goToCard(index)}
              className={`w-2 h-2 rounded-full carousel-dot ${
                activeIndex % totalCards === index
                  ? "bg-gray-600"
                  : "bg-gray-300"
              }`}
            ></button>
          ))}
        </div>
      )}

      {/* Navigation arrows */}
      {!!shouldShowArrows && (
        <div className="absolute top-1/2 transform -translate-y-1/2 w-full flex justify-between px-4">
          <button
            onClick={prevCard}
            className="bg-primary-400 text-white p-2 rounded-full"
          >
            <Icon
              name={"chevronLeft"}
              backgroundColor="transparent"
              size="lg"
              textColor={"white"}
              centered={false}
              padding={false}
            />
          </button>
          <button
            onClick={nextCard}
            className="bg-primary-400 text-white p-2 rounded-full"
          >
            <Icon
              name={"chevronRight"}
              backgroundColor="transparent"
              size="lg"
              textColor={"white"}
              centered={false}
              padding={false}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export const OperatorBehaviourCarousel = (props) => {
  const { profile, selectedBehaviour } = useOperatorContext();
  const indexOfSelectedBvr =
    !!selectedBehaviour && !!profile?.behaviours?.length
      ? profile.behaviours.findIndex(
          (b) => b.behaviourAcronym === selectedBehaviour.behaviourAcronym,
        )
      : 0;

  return <InfiniteCarousel {...props} initialIndex={indexOfSelectedBvr} />;
};

const CarouselSignol = (props) => {
  const { infinite, ...rest } = props;
  return !!infinite ? (
    <InfiniteCarousel {...rest} />
  ) : (
    <SimpleCarousel {...rest} />
  );
};

export const carouselSignolControls = {
  children: {
    propType: PropTypes.node.isRequired,
    description: "Carousel slide items",
    disable: true,
  },
  infinite: {
    propType: PropTypes.bool,
    description: "Carousel is infinite wheel or not",
    controlType: "boolean",
    defaultValue: false,
  },
  showDots: {
    propType: PropTypes.bool,
    description: "Show dots under cards",
    controlType: "boolean",
    defaultValue: true,
  },
  showArrows: {
    propType: PropTypes.bool,
    description: "Show side arrows",
    controlType: "boolean",
    defaultValue: true,
  },
};

CarouselSignol.propTypes = AppUtils.getPropTypeFromControls(
  carouselSignolControls,
);

export default CarouselSignol;
